<template>
  <esg-base-chart max-width="768px">
    <loading :active="loading"
             :z-index="1000"
             :is-full-page="false"/>
    <div :class="{ hidden: loading }"
         class="d-flex align-items-center justify-content-around">
      <div v-show="!loading"
           class="d-none d-md-flex flex-column align-items-center justify-content-center w-25">
        <div class="label">연간 배출량</div>
        <div class="emission">{{ formatNumber(total, 0) }}</div>
        <div class="unit">tCO2e</div>
      </div>
      <div class="w-75 flex-fill">
        <apex-chart type="bar"
                    ref="chart"
                    :width=this.width
                    :height=this.height
                    :options="chartOptions"
                    :series="series"/>
      </div>
    </div>
  </esg-base-chart>
</template>

<script>
import EsgBaseChart from "@/components/esg/base/EsgBaseChart.vue";

export default {
  components: { EsgBaseChart },
  props: {
    width: {
      type: String,
      default: '100%',
    },
    height: {
      type: String,
      default: '250px',
    },
    corporation: {
      type: Object,
      required: true,
    },
    businessLocation: {
      type: Object,
      required: true
    },
  },
  beforeDestroy() {
    if (this.abortController) {
      this.abortController.abort();
    }
  },
  data() {
    return {
      loading: false,
      total: 0,
      series: [],
      chartOptions: {
        chart: {
          stacked: true,
          stackType: "normal",
          fontFamily: 'NanumGothic, serif',
          toolbar: {
            show: false
          },
          zoom: {
            enabled: false
          }
        },
        plotOptions: {  //=> bar option
          bar: {
            horizontal: false,
            dataLabels: {
              position: 'top'
            }
          },
        },
        xaxis: { //x 축
          categories: [],
          crosshairs: { show: false }
        },
        dataLabels: {
          enabled: false,
          formatter: val => this.formatNumber(val, 0),
          style: {
            fontWeight: 'normal',
            fontFamily: 'NanumGothic',
            colors: ['#000']
          },
          offsetY: -20
        },
        yaxis: {
          show: true,
          title: {
            text: '탄소 배출량 (tCO2)',
            style: {
              fontSize: 12,
              fontWeight: 'normal'
            }
          },
          labels: {
            formatter: val => this.compactFormatNumber(val, 0)
          }
        },
        tooltip: {
          y: {
            formatter: val => this.formatNumber(val) + " tCO2"
          }
        },
        grid: {
          show: false
        },
        legend: {
          position: 'bottom',
        },
      },
    }
  },
  watch: {
    businessLocation: {
      deep: true,
      immediate: true,
      handler() {
        this.fetchData();
      }
    }
  },
  methods: {
    async fetchData() {
      if (!this.businessLocation || !this.businessLocation.business_location_code)
        return;

      try {
        this.loading = true;

        let year = new Date().getFullYear();
        let years = [year];
        let scopes = [1, 2];
        let args = [{
          years,
          scopes,
          corporations: [this.corporation.code],
          businessLocations: [this.businessLocation.business_location_code],
          groupKeys: ['year', 'month', 'scope', 'business_location_code']
        }];

        let data = new FormData()
        data.append('path', 'esg/ghg_emission/main');
        data.append('json', JSON.stringify(args));

        this.abortController = new AbortController();
        let response = await axios.post('esg/lca/run/python/chart-data', data, {
          headers: { Authorization: accessToken() },
          signal: this.abortController.signal
        });

        if (!response.data.data) {
          this.series = [];
          return;
        }

        let list = JSON.parse(response.data.data)[0].data;
        let months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

        this.setTotal(list);
        this.setSeries(list, months, scopes);
        this.setLabels(months);
      } catch (error) {
        this.loading = false;
        await this.showError(error);
      } finally {
        this.loading = false;
        this.refreshChart();
      }
    },
    setTotal(list) {
      this.total = list.map(row => row.values).reduce((a, b) => a + b, 0)
    },
    setSeries(list, months, scopes) {
      let series = []
      scopes.forEach(scope => {
        series.push({
          name: 'Scope ' + scope,
          data: list.filter(row => row.scope === scope).map(row => row.values)
        })
      });
      this.series = series;
    },
    setLabels(months) {
      this.chartOptions.xaxis.categories = months.map(month => month + '월');
    },
  }
}
</script>

<style scoped>
.total {
  font-family: NanumGothic, serif;
  color: black;
}

.label {
  font-size: 1.25rem;
  font-weight: 500;
}

.emission {
  font-size: 1.5rem;
  font-weight: 600;
}

.unit {
  font-size: 1rem;
  color: #555;
}

.hidden {
  display: none !important;
}
</style>
